import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { HomeIcon, PlusIcon, SearchIcon, FilterIcon, MapPinIcon, BedIcon, BathIcon, MaximizeIcon, MoreVerticalIcon, EditIcon, EyeIcon, ShareIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRental } from '../../contexts/RentalContext';
export function Listings() {
  const {
    listings
  } = useRental();
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'draft' | 'archived'>('all');
  const stats = [{
    label: 'Anunțuri Active',
    value: listings.filter(l => l.status === 'active').length,
    color: 'text-emerald-600',
    bg: 'bg-emerald-100'
  }, {
    label: 'În Lucru (Draft)',
    value: listings.filter(l => l.status === 'draft').length,
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  }, {
    label: 'Arhivate',
    value: listings.filter(l => l.status === 'archived').length,
    color: 'text-slate-600',
    bg: 'bg-slate-100'
  }, {
    label: 'Total Vizualizări',
    value: '1,245',
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }];
  const filteredListings = filterStatus === 'all' ? listings : listings.filter(l => l.status === filterStatus);
  return <div className="min-h-screen pb-20" data-id="element-10335">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#132C53] to-[#1e4278] pb-16 pt-10" data-id="element-10336">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-10337" />
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-10338"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10339">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-10340">
            <div className="text-center md:text-left" data-id="element-10341">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200
            }} className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mb-6 shadow-2xl md:hidden" data-id="element-10342">
                <HomeIcon className="w-8 h-8 text-white" data-id="element-10343" />
              </motion.div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 tracking-tight" data-id="element-10344">
                Anunțuri{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#357BEB] to-[#21A366]" data-id="element-10345">
                  Imobiliare
                </span>
              </h1>
              <p className="text-lg text-slate-300 max-w-2xl" data-id="element-10346">
                Gestionează portofoliul tău de proprietăți
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-10347">
              <Link to="/inchiriere/anunturi/nou" className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all" data-id="element-10348">
                <PlusIcon className="w-5 h-5" data-id="element-10349" />
                <span data-id="element-10350">Adaugă Proprietate</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10" data-id="element-10351">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-10352">
          {stats.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-slate-100" data-id="element-10353">
              <p className="text-sm text-slate-500 font-medium mb-1" data-id="element-10354">
                {stat.label}
              </p>
              <div className="flex items-center space-x-2" data-id="element-10355">
                <span className={`text-2xl sm:text-3xl font-bold ${stat.color}`} data-id="element-10356">
                  {stat.value}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8" data-id="element-10357">
          <div className="flex items-center space-x-2 bg-white p-1 rounded-xl border border-slate-200 shadow-sm w-full sm:w-auto overflow-x-auto" data-id="element-10358">
            {[{
            id: 'all',
            label: 'Toate'
          }, {
            id: 'active',
            label: 'Active'
          }, {
            id: 'draft',
            label: 'Draft'
          }, {
            id: 'archived',
            label: 'Arhivate'
          }].map(tab => <button key={tab.id} onClick={() => setFilterStatus(tab.id as any)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${filterStatus === tab.id ? 'bg-blue-50 text-blue-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`} data-id="element-10359">
                {tab.label}
              </button>)}
          </div>

          <div className="flex items-center space-x-3 w-full sm:w-auto" data-id="element-10360">
            <div className="relative flex-1 sm:flex-none" data-id="element-10361">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" data-id="element-10362" />
              <input type="text" placeholder="Caută proprietăți..." className="w-full sm:w-64 pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm" data-id="element-10363" />
            </div>
            <button className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors shadow-sm" data-id="element-10364">
              <FilterIcon className="w-5 h-5" data-id="element-10365" />
            </button>
          </div>
        </div>

        {/* Listings Grid */}
        {filteredListings.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-10366">
            {filteredListings.map((listing, index) => <motion.div key={listing.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col h-full" data-id="element-10367">
                {/* Image Container */}
                <div className="relative h-56 overflow-hidden" data-id="element-10368">
                  <img src={listing.images[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80'} alt={listing.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" data-id="element-10369" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" data-id="element-10370"></div>

                  <div className="absolute top-4 left-4" data-id="element-10371">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold text-white backdrop-blur-md ${listing.status === 'active' ? 'bg-emerald-500/80' : listing.status === 'draft' ? 'bg-orange-500/80' : 'bg-slate-500/80'}`} data-id="element-10372">
                      {listing.status === 'active' ? 'Activ' : listing.status === 'draft' ? 'Draft' : 'Arhivat'}
                    </span>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-10373">
                    <p className="text-2xl font-bold" data-id="element-10374">{listing.price} €</p>
                    <p className="text-sm opacity-90 flex items-center" data-id="element-10375">
                      <MapPinIcon className="w-3 h-3 mr-1" data-id="element-10376" />
                      {listing.location.address}
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col" data-id="element-10377">
                  <h3 className="text-lg font-bold text-[#132C53] mb-4 line-clamp-2 min-h-[3.5rem]" data-id="element-10378">
                    {listing.title}
                  </h3>

                  <div className="grid grid-cols-3 gap-2 mb-6" data-id="element-10379">
                    <div className="flex flex-col items-center justify-center p-2 bg-slate-50 rounded-xl" data-id="element-10380">
                      <BedIcon className="w-4 h-4 text-slate-400 mb-1" data-id="element-10381" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-10382">
                        {listing.specs.rooms} Cam.
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 bg-slate-50 rounded-xl" data-id="element-10383">
                      <BathIcon className="w-4 h-4 text-slate-400 mb-1" data-id="element-10384" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-10385">
                        {listing.specs.bathrooms} Băi
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 bg-slate-50 rounded-xl" data-id="element-10386">
                      <MaximizeIcon className="w-4 h-4 text-slate-400 mb-1" data-id="element-10387" />
                      <span className="text-xs font-bold text-slate-700" data-id="element-10388">
                        {listing.specs.surface} mp
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-10389">
                    <div className="flex items-center space-x-2" data-id="element-10390">
                      <Link to={`/inchiriere/anunturi/${listing.id}`} className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Editează" data-id="element-10391">
                        <EditIcon className="w-4 h-4" data-id="element-10392" />
                      </Link>
                      <Link to={`/inchiriere/anunturi/${listing.id}/preview`} className="p-2 rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors" title="Previzualizează" data-id="element-10393">
                        <EyeIcon className="w-4 h-4" data-id="element-10394" />
                      </Link>
                    </div>

                    <button className="flex items-center space-x-1 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors" data-id="element-10395">
                      <ShareIcon className="w-4 h-4" data-id="element-10396" />
                      <span data-id="element-10397">Distribuie</span>
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div> : <div className="text-center py-20 bg-white rounded-3xl shadow-xl border border-slate-200" data-id="element-10398">
            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-10399">
              <HomeIcon className="w-12 h-12 text-slate-300" data-id="element-10400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-10401">
              Nu ai niciun anunț {filterStatus !== 'all' ? filterStatus : ''}
            </h3>
            <p className="text-slate-500 mb-8 max-w-md mx-auto" data-id="element-10402">
              Adaugă prima ta proprietate pentru a începe promovarea.
            </p>
            <Link to="/inchiriere/anunturi/nou" className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-blue-500/30 inline-flex items-center space-x-2" data-id="element-10403">
              <PlusIcon className="w-5 h-5" data-id="element-10404" />
              <span data-id="element-10405">Adaugă Proprietate</span>
            </Link>
          </div>}
      </div>
    </div>;
}